"use client";

import type { ChangeEvent, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { ChoiceInput } from "../choice-input/choice-input";
import { Label } from "../label/label";
import { Text } from "../text/text";

export type RadioGroupOption = {
  value: string;
  label: string | ReactNode;
  description?: string;
  disabled?: boolean;
};

export type RadioGroupLabelVariant =
  | "body-md"
  | "body-md-bold"
  | "body-sm"
  | "body-sm-bold";

export type RadioGroupProps = {
  options: RadioGroupOption[];
  name?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  labelVariant?: RadioGroupLabelVariant;
  className?: string;
};

export function RadioGroup(props: Readonly<RadioGroupProps>): ReactNode {
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    props.onValueChange?.(e.target.value);
  }

  return (
    <div
      className={cn("flex flex-col gap-2", props.className)}
      role="radiogroup"
    >
      {props.options.map((option) => {
        const id = `${props.name ?? "radio-group"}-${option.value}`;
        const isDisabled = props.disabled === true || option.disabled === true;

        return (
          <div className="flex flex-col gap-1.5" key={option.value}>
            <Label
              className="flex cursor-pointer items-start gap-2"
              disabled={isDisabled}
              htmlFor={id}
            >
              <ChoiceInput
                className="mt-1"
                disabled={isDisabled}
                id={id}
                name={props.name ?? "radio-group"}
                onChange={handleChange}
                type="radio"
                value={option.value}
                {...(props.value === undefined
                  ? { defaultChecked: props.defaultValue === option.value }
                  : { checked: props.value === option.value })}
              />
              {typeof option.label === "string" ? (
                <Text variant={props.labelVariant ?? "body-md"}>
                  {option.label}
                </Text>
              ) : (
                <div>{option.label}</div>
              )}
            </Label>

            {option.description !== undefined && (
              <div className="ml-6">
                <Text color="muted" variant="body-sm">
                  {option.description}
                </Text>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
